import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Progress,
} from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { QrGenerationJob, JOB_STATUS_LABELS } from '../types';
import { toast } from 'react-toastify';
import JobErrorLogList from 'Components/Common/JobErrorLogList';
import ReasonPromptModal from 'Components/Common/ReasonPromptModal';
import { getQrJobErrorLogsApi } from 'helpers/fakebackend_helper';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('sinh_bo_so_qr');

interface ProgressDialogProps {
  isOpen: boolean;
  job: QrGenerationJob | null;
  periodName?: string | null;
  onClose: () => void;
  // Vi tri xep chong theo chieu doc — moi ky dang sinh song song co 1 offset rieng de khong de len nhau.
  topOffset?: number;
  // Thu gon/mo rong do component cha dieu khien (nhieu ky co the chay dong thoi, can biet chinh
  // xac panel nao dang mo de tinh offset, khong the de state thu gon nam rieng trong component nay).
  collapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
  // Goi API huy job — component cha (QrJobTracker) so huu dispatch de cap nhat Redux ngay sau khi
  // huy thanh cong, o day chi quan ly trang thai loading cua nut bam. Nhan ly do nguoi dung nhap
  // tu popup ReasonPromptModal ben duoi (bat buoc truoc khi goi API huy).
  onCancel?: (reason: string) => Promise<void> | void;
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
  isOpen,
  job,
  periodName,
  onClose,
  topOffset = 88,
  collapsed,
  onToggleCollapse,
  onCancel,
}) => {
  const location = useLocation();
  const [cancelling, setCancelling] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);

  const handleConfirmCancel = async (reason: string) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await onCancel?.(reason);
      setReasonModalOpen(false);
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (job?.status === 'completed') {
      const timer = setTimeout(() => {
        toast.success(st('sinh_qr_hoan_tat_ban_co_message'));
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [job?.status, onClose]);

  useEffect(() => {
    if (job?.status === 'failed' && job.errorMessage) {
      toast.error(st('sinh_qr_that_bai_error_message_message', { error_message: job.errorMessage }));
    }
    if (job?.status === 'stalled') {
      toast.warning(st('tien_trinh_sinh_qr_bi_dung_message'));
    }
  }, [job?.status, job?.errorMessage]);

  if (!isOpen || !job) return null;

  const totalTickets = job.totalTickets ?? 0;
  const processedTickets = job.processedTickets ?? 0;
  const progressPercent = Math.min(100, Math.max(0, Number(job.progressPercent ?? 0)));
  const statusLabel = JOB_STATUS_LABELS[job.status] ?? job.status ?? 'Pending';
  const canClose = job.status !== 'processing';
  const canCancel = job.status === 'pending' || job.status === 'processing';

  const getProgressColor = () => {
    switch (job.status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'danger';
      case 'stalled':
        return 'warning';
      case 'cancelled':
        return 'secondary';
      case 'processing':
        return 'primary';
      default:
        return 'info';
    }
  };

  const getIconClass = () => {
    switch (job.status) {
      case 'completed':
        return 'ri-check-double-line text-success';
      case 'failed':
        return 'ri-close-circle-line text-danger';
      case 'stalled':
        return 'ri-alarm-warning-line text-warning';
      case 'cancelled':
        return 'ri-forbid-line text-secondary';
      case 'processing':
        return 'ri-loader-4-line text-primary';
      default:
        return 'ri-time-line text-info';
    }
  };

  if (collapsed) {
    return (
      <div
        className="position-fixed"
        style={{ top: topOffset, right: 24, zIndex: 1060 }}
      >
        <button
          type="button"
          className="btn btn-light border shadow-lg d-flex align-items-center gap-2 rounded-pill py-2 px-3"
          title={st('phong_to_tien_trinh_sinh_bo_text', { value: periodName ? st('ky_period_name_text', { period_name: periodName }) : '' })}
          onClick={() => onToggleCollapse(false)}
        >
          <i className={`${getIconClass()} fs-5`}></i>
          <span className="fw-semibold small">{progressPercent.toFixed(0)}%</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="position-fixed"
      style={{
        top: topOffset,
        right: 24,
        zIndex: 1060,
        width: 'min(420px, calc(100vw - 32px))',
      }}
    >
      <Alert color="light" className="border shadow-lg mb-0" fade={false}>
        <div className="d-flex align-items-start gap-3">
          <i className={`${getIconClass()} fs-3 ${job.status === 'processing' ? 'spinner-border-sm' : ''}`}></i>
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div className="fw-semibold">{st('tien_trinh_sinh_bo_so_qr_text')}</div>
                {periodName && (
                  <div className="text-muted small text-truncate">
                    {st('ky_text')} {periodName}
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center gap-1">
                {canCancel && PageActionPermission.canDisplay(location.pathname, 'button-cancel-job') && (
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="py-0 px-2"
                    data-action-code="button-cancel-job"
                    data-action-name="Hủy tiến trình sinh QR"
                    disabled={cancelling}
                    onClick={() => setReasonModalOpen(true)}
                  >
                    {cancelling && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {st('huy_button')}
                  </Button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 lh-1"
                  aria-label={st('thu_nho_label')}
                  title={st('thu_nho_label')}
                  onClick={() => onToggleCollapse(true)}
                >
                  <i className="ri-subtract-line"></i>
                </button>
                {canClose && (
                  <Button close type="button" aria-label={st('dong_label')} onClick={onClose} />
                )}
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-between small mb-1">
                <span className="fw-semibold">{statusLabel}</span>
                <span className="fw-semibold">{progressPercent.toFixed(2)}%</span>
              </div>
              <Progress
                value={progressPercent}
                color={getProgressColor()}
                style={{ height: '10px' }}
                animated={job.status === 'processing'}
              />
            </div>

            <div className="d-flex justify-content-between text-muted small mt-2">
              <span>{st('da_xu_ly_title')}</span>
              <span className="fw-semibold">
                {processedTickets.toLocaleString('vi-VN')} / {totalTickets.toLocaleString('vi-VN')}
              </span>
            </div>

            {job.status === 'cancelled' && (
              <div className="small mt-2 text-muted">
                {st('da_huy_theo_yeu_cau_nguoi_text')}
              </div>
            )}

            {(job.status === 'failed' || job.status === 'stalled') && (
              <>
                {job.errorMessage && (
                  <div className={`small mt-2 text-break ${job.status === 'stalled' ? 'text-warning' : 'text-danger'}`}>
                    {job.errorMessage}
                  </div>
                )}
                <div className="text-muted small mt-2 fst-italic">
                  {st('bam_sinh_bo_so_qr_de_text')}
                </div>
                <JobErrorLogList fetchLogs={() => getQrJobErrorLogsApi(job.lotteryPeriodId)} />
              </>
            )}
          </div>
        </div>
      </Alert>

      <ReasonPromptModal
        isOpen={reasonModalOpen}
        title={st('huy_tao_ve_title')}
        label={st('ly_do_huy_tao_ve_label')}
        confirmLabel={st('huy_tao_ve_title')}
        submitting={cancelling}
        onCancel={() => setReasonModalOpen(false)}
        onSubmit={handleConfirmCancel}
      />
    </div>
  );
};

export default ProgressDialog;
